import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
    Button,
    ClickAwayListener,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    makeStyles,
    MenuItem,
    Paper,
    Popper,
    Select,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';

import { Block } from '@klp/design/Block';

import {
    useGetConsultantsQuery,
    useAssignProcessMutation,
    Activities,
    RegistrationStatuses,
    useGetContractRegistrationProcessQuery,
    ConsultantRoles,
    useGetContractToRegistrationQuery,
    ConsultantReviewerRoles,
} from '../services/rtkApi';
import type { Bruker } from '@klp/user/types';
import type { IContractToRegistrationProcessActivity } from '../classes/ContractToRegistrationProcessResponse';
import { Loader } from '@klp/design/Loader';
import { Typography } from '@klp/design/Typography';
import { ActionButton } from '@klp/design/Action';
import {
    ContractManagementRoutes,
    ContractManagementTabs,
} from '@forvalter/routing/Routes';
import { useHistory } from 'react-router-dom';
import { Tooltip } from '@klp/design/Tooltip';
import { useSignalR } from '../services/signalR';
import { contractToRegistrationProcessSelectors } from '../contractToRegistrationSlice';
import { useCurrentUser } from '@klp/auth';
import { showToast } from '@klpkonsern/ui';

type ConsultantSelectorProps = {
    activity?: {
        [key in
            | Activities.UTFOR_REGISTRERING_D365
            | Activities.TILDEL_OKONOMI]?: IContractToRegistrationProcessActivity;
    };
    status?: {
        [key in
            | RegistrationStatuses.TILDELT_OKONOMI
            | RegistrationStatuses.REGISTRERING_D365]?: string;
    };
    filter?: 'forvalter' | 'økonomi';
    completeTask: boolean;
    completeStepCallback?: () => void;
    isDisabled?: boolean;
    submitIsDisabled?: boolean;
    isLoading?: boolean;
    sortBy?:
        | ConsultantRoles.FORVALTER
        | ConsultantRoles.DRIFTS_FORVALTER
        | ConsultantRoles.HANDEL_FORVALTER
        | ConsultantRoles.KONTOR_FORVALTER
        | ConsultantRoles.OKONOMI
        | ConsultantRoles.QA_OKONOMI;
    showDialogConfirmationForStep?: boolean;
};

const useStyles = makeStyles((theme) => ({
    selector: {
        width: '100%',
        minWidth: theme.spacing(25),
    },
    buttonWrapper: {
        width: 'max-content',
        cursor: 'not-allowed',
    },
}));

export const ConsultantSelector: React.FC<ConsultantSelectorProps> = ({
    filter,
    status,
    activity,
    completeTask,
    isDisabled,
    submitIsDisabled,
    isLoading,
    sortBy,
    showDialogConfirmationForStep,
    completeStepCallback,
}) => {
    const { t, i18n } = useTranslation();
    const history = useHistory();
    const { kontraktId } = useParams<{ kontraktId: string }>();
    const { currentUser } = useCurrentUser();
    const classes = useStyles();

    const [selectedConsultant, setSelectedConsultant] = useState<
        string | undefined
    >('');
    const [showDialog, setShowDialog] = useState({
        showDialogConfirmation: showDialogConfirmationForStep ?? false,
        openDialog: false,
    });
    const anchorRef = useRef(null);

    const { connected } = useSignalR();

    const registrationProcessDataSignalR = useSelector(
        contractToRegistrationProcessSelectors,
    );

    const { data: contractToRegistration } = useGetContractToRegistrationQuery({
        kontraktId,
    });

    const {
        data: registerProcessDataRest,
        isFetching: registerProcessFetching,
    } = useGetContractRegistrationProcessQuery({
        kontraktDokumentId: kontraktId,
        startNewProcess: false,
        signalRConnected: connected,
        culture: i18n.language,
    });

    const registerProcessData = connected
        ? registrationProcessDataSignalR
        : registerProcessDataRest;

    const { data: consultants, isLoading: consultantsIsLoading } =
        useGetConsultantsQuery();

    const [
        assignConsultant,
        { isLoading: assignConsultantLoading, isError: assignConsultantError },
    ] = useAssignProcessMutation();

    const sortedConsultants = useMemo(() => {
        return consultants?.filter((p) => {
            if (filter === 'forvalter') {
                return p.isForvalter === true;
            }

            if (filter === 'økonomi') {
                return p.isOkonomi === true;
            }
            return p;
        });
    }, [consultants, filter]);

    const currentActivity =
        activity?.[Activities.TILDEL_OKONOMI] ||
        activity?.[Activities.UTFOR_REGISTRERING_D365];

    const currentStatus =
        status?.[RegistrationStatuses.TILDELT_OKONOMI] ||
        status?.[RegistrationStatuses.REGISTRERING_D365];

    const { showDialogConfirmation, openDialog } = showDialog;

    useEffect(() => {
        if (sortBy === ConsultantRoles.OKONOMI) {
            const okonomiBuildingConsultant =
                contractToRegistration?.bygg?.oekonomiEPost ?? undefined;

            setSelectedConsultant(
                registerProcessData?.ansvarligOekonomiId ??
                    okonomiBuildingConsultant,
            );
        }

        if (sortBy === ConsultantRoles.QA_OKONOMI) {
            const qaRegionConsultant =
                sortedConsultants?.filter((p) =>
                    currentUser?.byggRegioner.includes(p.qaRegion ?? ''),
                )?.[0]?.brukerId ?? undefined;

            setSelectedConsultant(
                registerProcessData?.ansvarligOekonomiQAId ??
                    qaRegionConsultant,
            );
        }

        if (sortBy === ConsultantRoles.FORVALTER) {
            setSelectedConsultant(
                registerProcessData?.ansvarligForvalterId ?? undefined,
            );
        }
    }, [
        sortBy,
        registerProcessData,
        sortedConsultants,
        contractToRegistration,
        currentUser,
    ]);

    const handlePreSelectedConsultant = () => {
        if (!sortBy) {
            return;
        }

        if (sortBy === ConsultantRoles.OKONOMI) {
            return registerProcessData?.ansvarligOekonomiId;
        }

        if (sortBy === ConsultantRoles.QA_OKONOMI) {
            return registerProcessData?.ansvarligOekonomiQAId;
        }

        if (sortBy === ConsultantRoles.FORVALTER) {
            return registerProcessData?.ansvarligForvalterId;
        }

        return;
    };

    const handleCompleteStep = async () => {
        if (completeStepCallback) {
            completeStepCallback();
        }

        if (showDialogConfirmation) {
            // Sjekker om den valgte konsulenten er en QA konsulent - konverterer til boolean
            const matchingQAConsultant = !!sortedConsultants?.find(
                (consultant) =>
                    consultant?.isQAOkonomi &&
                    consultant?.brukerId === selectedConsultant,
            );

            if (
                !matchingQAConsultant &&
                sortBy === ConsultantRoles.QA_OKONOMI
            ) {
                setShowDialog({ showDialogConfirmation, openDialog: true });
                return;
            }

            if (sortBy === ConsultantRoles.OKONOMI) {
                setShowDialog({ showDialogConfirmation, openDialog: true });
                return;
            }

            if (sortBy === ConsultantRoles.FORVALTER) {
                setShowDialog({ showDialogConfirmation, openDialog: true });
                return;
            }
        }

        if (!selectedConsultant || !currentStatus || !currentActivity) {
            return;
        }

        const handleRole = () => {
            if (sortBy === ConsultantRoles.OKONOMI) {
                return ConsultantReviewerRoles.OKONOMI;
            }

            if (sortBy === ConsultantRoles.QA_OKONOMI) {
                return ConsultantReviewerRoles.QA_OKONOMI;
            }

            if (sortBy === ConsultantRoles.FORVALTER) {
                return ConsultantReviewerRoles.FORVALTER;
            }

            return undefined;
        };

        const role = handleRole();

        await assignConsultant({
            ansvarlig: selectedConsultant,
            fullforAktivitet: completeTask,
            registreringStatus: currentStatus,
            kontraktDokumentId: kontraktId,
            rolle: role,
        });
    };

    const handleCancel = () => {
        history.push(
            ContractManagementRoutes.tab(ContractManagementTabs.registrering),
        );
    };

    const handleConfirmSelectedConsultant = async () => {
        if (!selectedConsultant || !currentStatus || !currentActivity) {
            return;
        }

        setShowDialog({ showDialogConfirmation, openDialog: false });

        await assignConsultant({
            ansvarlig: selectedConsultant,
            fullforAktivitet: completeTask,
            registreringStatus: currentStatus,
            kontraktDokumentId: kontraktId,
        });

        return;
    };

    const handleAbortSelectedConsultant = () => {
        setShowDialog({ showDialogConfirmation, openDialog: false });
    };

    const renderMenuItem = (entry?: Bruker, index?: number) => (
        <MenuItem
            onClick={() => setSelectedConsultant(entry?.brukerId)}
            key={`${entry?.brukerId}-${index}`}
            value={entry?.brukerId}
            style={
                entry?.isQAOkonomi && sortBy === ConsultantRoles.QA_OKONOMI
                    ? { fontWeight: 'bold' }
                    : {}
            }
        >
            {entry?.fornavn} {entry?.etternavn}
        </MenuItem>
    );

    if (assignConsultantError) {
        showToast({
            body: t('contractRegistration.error'),
            type: 'error',
        });
    }

    const componentIsLoading =
        consultantsIsLoading ||
        registerProcessFetching ||
        assignConsultantLoading ||
        isLoading;

    if (componentIsLoading) return <Loader isLoading={componentIsLoading} />;

    const componentSubmitIsDisabled =
        (completeTask && submitIsDisabled) ||
        !selectedConsultant ||
        assignConsultantLoading;

    return (
        <Block paddingVertical="compact" paddingTop="compact">
            {currentActivity?.utfortDato === null || !completeTask ? (
                <FormControl
                    className={classes.selector}
                    data-cy="ConsultantSelector"
                    disabled={isDisabled}
                >
                    <InputLabel id="ConsultantSelector__Label">
                        {t(
                            `checkOffContract.step.${currentActivity?.taskDefinitionId}.tertiaryTitle`,
                        )}
                    </InputLabel>
                    <Select
                        labelId="ConsultantSelector__Label"
                        id="ConsultantSelector"
                        data-cy="ConsultantSelector"
                        value={selectedConsultant}
                        defaultValue={selectedConsultant}
                    >
                        {sortedConsultants?.map((entry, index) =>
                            renderMenuItem(entry, index),
                        )}
                    </Select>
                </FormControl>
            ) : (
                <Typography>
                    {t(
                        `checkOffContract.step.${currentActivity?.taskDefinitionId}.chosenConsultant`,
                    )}{' '}
                    {handlePreSelectedConsultant()}
                </Typography>
            )}
            <Block paddingTop="compact">
                <Grid container={true} direction="row">
                    {!completeTask && (
                        <Grid item={true}>
                            {' '}
                            <Block paddingRight="compact" paddingTop="compact">
                                <ActionButton
                                    actionType="cancel"
                                    translation="checkOffContract.cancelButton"
                                    onClick={handleCancel}
                                />
                            </Block>
                        </Grid>
                    )}
                    {(currentActivity?.utfortDato === null ||
                        !completeTask ||
                        !selectedConsultant) && (
                        <Grid item={true}>
                            <Block paddingTop="compact">
                                <>
                                    {showDialogConfirmation && openDialog && (
                                        <ClickAwayListener
                                            onClickAway={
                                                handleAbortSelectedConsultant
                                            }
                                        >
                                            <Popper
                                                open={openDialog}
                                                anchorEl={anchorRef.current}
                                                placement="bottom"
                                                transition={true}
                                                style={{
                                                    maxWidth: '350px',
                                                }}
                                            >
                                                <Paper
                                                    style={{
                                                        padding: '20px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <Block
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent:
                                                                'space-between',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="h6"
                                                            gutterBottom={true}
                                                        >
                                                            {t(
                                                                'checkOffContract.dialog.title',
                                                            )}
                                                        </Typography>
                                                    </Block>
                                                    <Block
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent:
                                                                'center',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        <IconButton
                                                            disabled={
                                                                assignConsultantLoading
                                                            }
                                                            onClick={
                                                                handleConfirmSelectedConsultant
                                                            }
                                                            color="secondary"
                                                        >
                                                            <CheckIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            disabled={
                                                                assignConsultantLoading
                                                            }
                                                            onClick={
                                                                handleAbortSelectedConsultant
                                                            }
                                                            color="primary"
                                                        >
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Block>
                                                </Paper>
                                            </Popper>
                                        </ClickAwayListener>
                                    )}
                                    <Tooltip
                                        translation="checkOffContract.tooltip"
                                        hideTooltip={!componentSubmitIsDisabled}
                                    >
                                        <Button
                                            disabled={componentSubmitIsDisabled}
                                            ref={anchorRef}
                                            variant="contained"
                                            color="secondary"
                                            onClick={handleCompleteStep}
                                        >
                                            {t(
                                                `checkOffContract.step.${currentActivity?.taskDefinitionId}.cta`,
                                            )}
                                        </Button>
                                    </Tooltip>
                                </>
                            </Block>
                        </Grid>
                    )}
                </Grid>
            </Block>
        </Block>
    );
};
